<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :feedbackOperationData="feedbackOperationData"
    :activeStep="activeStep"
    :scrollValue="scrollValue"
    :stepperParams="stepperParams"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleFull="t('cards.request.virtualCard.tokenTitleFull')"
    @clearScrollToValue="scrollValue = null"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, createNewCard)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <component
        v-if="!applyErrorState"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :selectedCardInfo="selectedCardInfo"
        @consultNewCard="navigateByName(routeList.CARDS_CONSULT_NAME)"/>
        <!-- Conditions Element -->
        <div
        v-if="activeStep === 1 && !showLoader && !applyErrorState"
        class="conditions-wrapper">
            <!-- Divider -->
            <divider-generic icon="pencil-line" :title="t('cards.request.virtualCard.dividerLeftConditions')" />
            <!-- Note -->
            <text-block-generic :params="{ content: t('cards.request.virtualCard.conditionsNote', { product: selectedCardInfo.description }) }" />
            <!-- Checkbox -->
            <selector-checkbox
            :label="t('cards.request.virtualCard.link', { link: selectedCardInfo.infoSheetUrl, target: '_blank' })"
            :selected="formData.conditions"
            :errorState="conditionsError.errorState"
            :errorMessage="conditionsError.errorMessage"
            @clicked="conditionsCheckboxClickHandler"/>
            <div id="conditions"></div>
        </div>
    </wrapper-features-templates-operations>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ToastGeneric from '@/components/generic/toasts/Generic'
import SelectorCheckbox from '@/components/generic/selectors/checkbox/Checkbox'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'

/* Assets and Helpers */
import { clearObject } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'
import { emptyObjectConstructor, errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const props = defineProps({
    selectedCardInfo: Object
})

/* mapActions & mapGetters */
const { getMetadataForCardRequest, requestNewCard } = mapActions(moduleMapper.CARDS_REQUEST)
const { getMetaData } = mapGetters(moduleMapper.CARDS_REQUEST)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const scrollValue = ref(null)

const formData = ref({
    conditions: null
})

const conditionsError = ref({
    errorState: false,
    errorMessage: null
})

const conditionsCheckboxClickHandler = () => {
    clearConditionsError()

    formData.value.conditions = !formData.value.conditions
}

const clearConditionsError = () => {
    conditionsError.value = {
        errorState: false,
        errorMessage: null
    }
}

/*************************************************
*                  Steps Data                    *
*************************************************/
const activeStep = ref(1)

const goToStep = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const stepperParams = ref([
    { description: t('cards.charge.virtual.stepperParams.data') },
    { description: t('cards.charge.virtual.stepperParams.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/cards/request/virtualCard/Step' + stepNumber))
})

/*************************************************
*                                                *
*                    Requests                    *
*                                                *
*************************************************/
const requestMetaDataForCardRequest = async () => {
    showLoader.value = true
    applyErrorState.value = false

    const payload = {
        cardType: props.selectedCardInfo.subProductCode,
        product: props.selectedCardInfo.productCode
    }

    try {
        await getMetadataForCardRequest(payload)
        showLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, requestMetaDataForCardRequest)
    }
}

const createNewCard = async () => {
    showOverlayLoader.value = true

    const payload = {
        productCode: props.selectedCardInfo.productCode,
        subProductCode: props.selectedCardInfo.subProductCode,
        cardCustomerName: getMetaData.value.nameOnCard
    }

    try {
        await requestNewCard(payload)
        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, createNewCard)
    }
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const feedbackOperationData = ref(null)

const successHandler = data => {
    scrollValue.value = 0

    feedbackOperationData.value = {
        status: data.errorLevel,
        code: data.code,
        data: data.data,
        title: data.message
    }

    goToStep(3)
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { goToStep(1) })
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const footerButtonsParams = computed(() => {
    return [{
        label: t('cards.request.virtualCard.footerButtons.clear'),
        callbackFunction: clearAllFields,
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('cards.request.virtualCard.footerButtons.forward'),
        callbackFunction: validateInputs,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 1
    }, {
        label: t('cards.request.virtualCard.footerButtons.edit'),
        icon: 'arrow-left-full',
        callbackFunction: () => { goToStep(1) },
        step: 2
    }, {
        label: t('cards.request.virtualCard.footerButtons.validate'),
        callbackFunction: createNewCard,
        icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        step: 2
    }]
})

const clearAllFields = () => {
    formData.value = clearObject(formData.value)
    formData.value = {
        conditions: false
    }
    clearConditionsError()
    scrollValue.value = 0
    feedbackOperationData.value = null
}

const validateInputs = () => {
    if (!formData.value.conditions) {
        conditionsError.value.errorState = true
        conditionsError.value.errorMessage = t('mandatoryFieldError')

        scrollValue.value = getElementOffsetTopPosition('conditions')
    } else goToStep(2)
}

requestMetaDataForCardRequest()
</script>

<style lang="scss" scoped>
.conditions-wrapper {
	width: 508px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: 24px;
}
</style>

<style lang="scss" scoped>
</style>
